import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Alert, EmptyState, ErrorState, PageHeader, SelectField, Skeleton, TextField, Button } from "../components/ui.tsx";
import { AxisTag } from "../components/status.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { ReopenRequestsPanel } from "../components/reopen.tsx";
import { get } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import type { Axis, Unit } from "../lib/types.ts";
import { formatInstantBR } from "../../shared/dates.ts";
import { usePageTitle, useUrlState } from "../lib/nav.tsx";
import { initiativeLabel, krLabel } from "../../shared/numbering.ts";

interface QueueRow {
  id: string; revision_number: number; submitted_at: string; submitted_by: string; initiative_id: string; title: string;
  status: string; axis_id: string; axis_name: string; axis_color: string; unit_name: string | null; weight_total: number; action_count: number;
  number: number | null; kr_number: number | null;
}

/** T08: one row per current initiative (its current revision), oldest pending first. */
export function ReviewQueuePage() {
  const plan = usePlan();
  usePageTitle("Revisões", plan.name);
  const [u, setU] = useUrlState({ status: "in_review", axis_id: "", unit_id: "", submitted_from: "", submitted_to: "", page: "1" });
  const { page: pageStr, ...f } = u;
  const page = Number(pageStr) || 1;
  const setPage = (fn: (p: number) => number) => setU({ page: String(fn(page)) });
  const qs = new URLSearchParams(Object.entries({ ...f, page: String(page), page_size: "50" }).filter(([, v]) => v)).toString();
  const q = useQuery({
    queryKey: planKey(plan.id, "revisions", qs),
    queryFn: () => get<{ data: QueueRow[]; meta: { total: number; page_size: number } }>(`/plans/${plan.id}/revisions?${qs}`),
    placeholderData: keepPreviousData,
    enabled: plan.capabilities.can_review,
  });
  const axes = useQuery({ queryKey: planKey(plan.id, "axes"), queryFn: async () => (await get<{ data: Axis[] }>(`/plans/${plan.id}/axes`)).data });
  const units = useQuery({ queryKey: planKey(plan.id, "units"), queryFn: async () => (await get<{ data: Unit[] }>(`/plans/${plan.id}/units`)).data });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setU({ [k]: e.target.value, page: "1" });

  if (!plan.capabilities.can_review) return <Alert tone="warn">A fila de revisões é exclusiva do instrutor.</Alert>;
  const pages = q.data ? Math.max(1, Math.ceil(q.data.meta.total / q.data.meta.page_size)) : 1;
  return (
    <>
      <PageHeader title="Revisões do planejamento" subtitle="Iniciativas submetidas, da mais antiga para a mais recente." />
      <ReopenRequestsPanel />
      <div className="grid gap-x-4 sm:grid-cols-2 lg:grid-cols-5">
        <SelectField label="Situação" value={f.status} onChange={set("status")}>
          <option value="in_review">Em revisão</option>
          <option value="changes_requested">Ajustes solicitados</option>
          <option value="validated">Validadas</option>
        </SelectField>
        <SelectField label="Eixo" value={f.axis_id} onChange={set("axis_id")}>
          <option value="">Todos</option>
          {(axes.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </SelectField>
        <SelectField label="Unidade" value={f.unit_id} onChange={set("unit_id")}>
          <option value="">Todas</option>
          {(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </SelectField>
        <TextField label="Submetidas desde" type="date" value={f.submitted_from} onChange={set("submitted_from")} />
        <TextField label="Submetidas até" type="date" value={f.submitted_to} onChange={set("submitted_to")} />
      </div>
      {q.isPending ? <Skeleton lines={5} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.data.data.length === 0 ? (
        <EmptyState title={f.status === "in_review" ? "Nenhuma iniciativa aguardando revisão." : "Nenhuma iniciativa nesta situação."}>
          Quando um grupo submeter uma iniciativa, ela aparecerá aqui.
        </EmptyState>
      ) : (
        <>
          <ul className="divide-y divide-line overflow-hidden rounded-panel border border-line bg-surface shadow-e1">
            {q.data.data.map((r) => (
              <li key={r.id}>
                <GuardedLink href={`/planos/${plan.id}/revisoes/${r.id}`} className="elevate block px-4 py-3">
                  <span className="block text-[11px] font-bold tracking-wide text-muted uppercase">{krLabel(r.kr_number)} · {initiativeLabel(r.number)}</span>
                  <span className="flex flex-wrap items-center gap-2"><span className="font-semibold text-ink">{r.title}</span><AxisTag name={r.axis_name} color={r.axis_color} /></span>
                  <span className="mt-0.5 block text-xs text-muted">
                    Versão submetida {r.revision_number} · {r.unit_name ?? "sem unidade"} · {r.action_count} ações · {r.weight_total} pontos · enviada por {r.submitted_by} em {formatInstantBR(r.submitted_at)}
                  </span>
                </GuardedLink>
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-sm">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span>Página {page} de {pages}</span>
              <Button variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
